import { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faBus,
  faCar,
  faCircleInfo,
  faPen,
  faPenToSquare,
  faTrailer,
  faTruck,
  faXmark
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { hasRole } from "auth/hasRole"
import { AppContext } from "components/AppContextProvider"
import { CircleIcon } from "components/icons/CircleIcon"
import PaperBox from "components/PaperBox"
import { allRoles } from "generated/roles"
import { useContext, useState } from "react"
import { TextCell, TextCell2 } from "./components/TextCell"
import { t } from "i18next"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"
import { useTranslation } from "react-i18next"

const vehicleIcon = (variant: string) => {
  switch (variant) {
    case "Lastbil" || "LB":
      return faTruck
    case "Personbil":
      return faCar
    case "Släpvagn":
      return faTrailer
    default:
      return faBus
  }
}

const StyledButton = styled(Button)({
  padding: 0,
  marginRight: "-12px",
  marginTop: "-20px",
  minWidth: "50px",
  minHeight: "50px"
})

interface HeaderProps {
  status: number
  headerData: any
  openForm: () => void
}

const StyledPaperBoxVehicleCard = styled(PaperBox)(({ isArchived, theme }) => ({
  backgroundColor: isArchived ? theme.palette.grey[100] : theme.palette.secondary.light,
  border: `1px solid ${theme.palette.secondary[300]}`,
  margin: 0,
  borderRadius: "10px"
}))

const StyledToolTip = styled(Tooltip)(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "(theme.palette as any).foreground.inverted",
    color: theme.palette.text.primary
  },
  "& .MuiTooltip-arrow": {
    color: (theme.palette as any).foreground.inverted
  }
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "0",
  top: "18px"
}))

const StyledFontAwesomeXmark = styled(FontAwesomeIcon)(({ theme }) => ({
  width: "20px",
  height: "20px"
}))

const CustomTooltipContent = ({ handleTooltipClose }: { handleTooltipClose: () => void }) => (
  <Grid container direction="column" spacing={2} p={2} position="relative">
    <StyledIconButton onClick={handleTooltipClose} size="small">
      <StyledFontAwesomeXmark icon={faXmark} />
    </StyledIconButton>
    <Grid item>
      <Typography variant="h4">{t("global.meter_setting", "Meter setting")}</Typography>
    </Grid>
    <Grid item>
      <Typography fontWeight="500" variant="labelSmall">
        {t("global.meter_setting", "Meter setting")}
      </Typography>
      <Typography variant="bodySmall" color="textSecondary">
        {t("vehicleregistry.manually_entered_meter_reading", "Manually entered meter reading")}
      </Typography>
    </Grid>
    <Grid item>
      <Typography fontWeight="500" variant="labelSmall">
        {t("vehicleregistry.tachograph_odometer_reading", "Tachograph odometer reading")}
      </Typography>
      <Typography variant="bodySmall" color="textSecondary">
        {t(
          "vehicleregistry.data_is_retrieved_from_the_tachograph",
          "Data is retrieved from the tachograph [once per day]"
        )}
      </Typography>
    </Grid>
    <Grid item>
      <Typography fontWeight="500" variant="labelSmall">
        {t("vehicleregistry.odometer_reading_previous_inspection", "Odometer reading previous inspection")}
      </Typography>
      <Typography variant="bodySmall" color="textSecondary">
        {t(
          "vehicleregistry.reported_odometer_reading_at_the_most",
          "Reported odometer reading at the most recent inspection obtained from the Swedish Transport Agency"
        )}
      </Typography>
    </Grid>
  </Grid>
)

const Header = ({ headerData, openForm }: HeaderProps) => {
  const appContext = useContext(AppContext)
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  const handleTooltipClose = () => {
    setOpen(false)
  }

  const handleTooltipOpen = () => {
    setOpen(true)
  }

  const CustomToolTip = () => (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box sx={{ marginTop: "-4px" }}>
        <StyledToolTip
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme.palette.background["paper"],
                color: theme.palette.text.primary,
                boxShadow: "0px 0px 65px 0px rgb(0 0 0 / 6%)"
              }
            }
          }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={<CustomTooltipContent handleTooltipClose={handleTooltipClose} />}>
          <IconButton onClick={handleTooltipOpen} size="small">
            <FontAwesomeIcon style={{ color: (theme.palette as any).primary[700] }} icon={faCircleInfo} />
          </IconButton>
        </StyledToolTip>
      </Box>
    </ClickAwayListener>
  )

  return (
    <Grid container direction="row" alignItems="flex-end" spacing={5}>
      <Grid item xs={12} xl={8}>
        <StyledPaperBoxVehicleCard isArchived={headerData.archived} theme={theme}>
          <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={5}>
            <Grid item>
              <Box component={Grid} container direction="row" marginBottom={3} alignItems="center">
                <CircleIcon size="lg" icon={vehicleIcon(headerData.fordonstyp)} />
                <Grid flexDirection="column">
                  <Typography marginLeft={2} variant="h2">
                    {headerData && headerData.regNumber}
                  </Typography>
                  <Typography marginLeft={2} variant="bodyMedium">
                    {headerData && headerData.fordonstyp}
                  </Typography>
                </Grid>
                {headerData.archived && (
                  <Box
                    sx={{ backgroundColor: (theme.palette as any).error[200], borderRadius: "20px" }}
                    px={3}
                    py={0.5}
                    ml={4}
                    mb={2}
                    color={"black"}>
                    <Typography variant="paragraphMedium">{t("global.inactive", "Inactive")}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            {hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin) && (
              <Grid>
                <IconButton onClick={() => openForm()} color="primary">
                  <FontAwesomeIcon color={(theme.palette as any).primary[700]} icon={faPen as IconProp} />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid container direction="row" spacing={8}>
            <Grid item>
              <TextCell2
                label={t("global.internal_number_short", "Internal No")}
                text={headerData && headerData.internnumber}
                icon={null}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("global.fabric", "Fabric")}
                text={headerData && headerData.fabrikat}
                icon={null}
              />
            </Grid>
            <Grid item style={{ display: "flex", alignItems: "center" }}>
              <TextCell2
                icon={
                  hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin) && (
                    <DynamicTooltip
                      iconPosition={true}
                      t={t}
                      title={t("global.meter_setting", "Meter setting")}
                      subtitle={t(
                        "vehicleregistry.vehicle_milage",
                        "The vehicle's mileage is based on the information below. The highest recorded value is used."
                      )}>
                      <Grid item>
                        <Typography fontWeight="600" variant="labelSmall">
                          {t(
                            "vehicleregistry.manually_entered_meter_reading",
                            "Manually entered meter reading"
                          )}
                        </Typography>
                        <Typography variant="bodySmall" color="textSecondary">
                          {t(
                            "vehicleregistry.odometer_reading_entered_manually_in",
                            "Odometer reading entered manually in the vehicle register."
                          )}
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Typography fontWeight="600" variant="labelSmall">
                          {t(
                            "vehicleregistry.odometer_reading_from_issue_report",
                            "Odometer reading from issue report"
                          )}
                        </Typography>
                        <Typography variant="bodySmall" color="textSecondary">
                          {t(
                            "vehicleregistry.odometer_reading_provided_in_connection",
                            "Odometer reading provided in connection with a fault report."
                          )}
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Typography fontWeight="600" variant="labelSmall">
                          {t(
                            "vehicleregistry.odometer_reading_at_the_previous_inspection",
                            "Odometer reading at the previous inspection"
                          )}
                        </Typography>
                        <Typography variant="bodySmall" color="textSecondary">
                          {t(
                            "vehicleregistry.odometer_reading_reported_at_the_most_recent_inspection",
                            "Odometer reading reported at the most recent inspection and retrieved from the Swedish Transport Agency."
                          )}
                        </Typography>
                      </Grid>
                    </DynamicTooltip>
                  )
                }
                label={t("global.meter_setting", "Meter setting")}
                text={headerData && headerData.meterSetting + " " + t("global.km", "km")}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("global.vehicle_type", "global.vehicle_type")}
                text={headerData && headerData.fordonstyp}
                icon={null}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("global.chassis_number", "Chassis number")}
                text={headerData && headerData.chassinummer}
                icon={null}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("global.department", "Department")}
                text={headerData && headerData.departmentName}
                icon={null}
              />
            </Grid>
            <Grid item>
              <TextCell2
                label={t("global.fuel", "Fuel")}
                text={
                  headerData && headerData.mainPropellant !== null
                    ? headerData.mainPropellant
                    : headerData.drivMedel1Kod
                }
              />
            </Grid>
          </Grid>
        </StyledPaperBoxVehicleCard>
      </Grid>
    </Grid>
  )
}

export default Header
